import { createContext, useEffect, useMemo, useState } from 'react';
import { Button, Modal, message } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { appActions } from '@nxsys/store/slices/app/reducer/appSlice';
import { socketYearEndProcess } from '@nxsys/services/sockets';
import GuidelinesStep from './steps/GuidelinesStep';
import WhatDoneStep from './steps/WhatDoneStep';
import P60Step from './steps/P60Step';
import SummaryStep from './steps/SummaryStep';
import ConfirmModal from './ConfirmModal';
import {
  InfoCircleOutlined,
  UnorderedListOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import KickOffRocketImg from '@nxsys/assets/images/kick-off/kick-off-rocket.png';
import KickOffCongratsImg from '@nxsys/assets/images/kick-off/kick-off-congrats.png';

interface YearEndProcess {
  taxYear: number;
  status: 'PROCESSING' | 'COMPLETED';
  progress: number;
  processingMessage?: string;
  p60Date?: string;
  totalSubcontractors: number;
  sentP60Count: number;
  totalP60NeedToSend: number;
}

interface KickOffContextType {
  p60issueDate: string | null;
  setP60issueDate: (date: string | null) => void;
  isCompleteNewYear: boolean;
  setIsCompleteNewYear: (v: boolean) => void;
  yearEndProcess: YearEndProcess | null;
  currentTaxYearStr: string;
  nextTaxYearStr: string;
}

export const KickOffContext = createContext<KickOffContextType>({
  p60issueDate: null,
  setP60issueDate: () => {},
  isCompleteNewYear: false,
  setIsCompleteNewYear: () => {},
  yearEndProcess: null,
  currentTaxYearStr: '',
  nextTaxYearStr: '',
});

interface ModalKickOffProps {
  open: boolean;
  onClose: () => void;
  yearEndData: YearEndProcess | null;
  taxYear: number;
}

const STEPS = {
  GUIDELINES: 'GUIDELINES',
  WHAT_DONE: 'WHAT_DONE',
  P60: 'P60',
  SUMMARY: 'SUMMARY',
} as const;

const ModalKickOff = ({
  open,
  onClose,
  yearEndData,
  taxYear,
}: ModalKickOffProps) => {
  const dispatch = useDispatch();
  const user = useSelector((state: AppState) => state.appStore.user);

  // Lock taxYear on open to prevent jumping during processing
  const [initialTaxYear] = useState(taxYear);

  const isCurrentYearProcess = yearEndData?.taxYear === initialTaxYear;
  const validYearEndProcess = isCurrentYearProcess ? yearEndData : null;

  const [yearEndProcess, setYearEndProcess] =
    useState<YearEndProcess | null>(validYearEndProcess);
  const [activeStep, setActiveStep] = useState<string>(
    validYearEndProcess ? STEPS.SUMMARY : STEPS.GUIDELINES,
  );
  const [p60issueDate, setP60issueDate] = useState<string | null>(null);
  const [isCompleteNewYear, setIsCompleteNewYear] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const currentTaxYearStr = initialTaxYear
    ? `${initialTaxYear}-${initialTaxYear + 1}`
    : '';
  const nextTaxYearStr = initialTaxYear
    ? `${initialTaxYear + 1}-${initialTaxYear + 2}`
    : '';

  // Init from API data
  useEffect(() => {
    if (yearEndProcess?.taxYear === initialTaxYear) {
      if (yearEndProcess.p60Date) {
        setP60issueDate(yearEndProcess.p60Date);
      }
      setIsCompleteNewYear(yearEndProcess.status === 'COMPLETED');
    }
  }, [yearEndProcess, initialTaxYear]);

  const contextValue: KickOffContextType = {
    p60issueDate,
    setP60issueDate,
    isCompleteNewYear,
    setIsCompleteNewYear,
    yearEndProcess,
    currentTaxYearStr,
    nextTaxYearStr,
  };

  const stepMenu = useMemo(
    () => [
      {
        key: STEPS.GUIDELINES,
        title: 'Guidelines',
        helperText: '',
        icon: InfoCircleOutlined,
        content: <GuidelinesStep />,
      },
      {
        key: STEPS.WHAT_DONE,
        title: 'What will be done?',
        helperText: '',
        icon: UnorderedListOutlined,
        content: <WhatDoneStep />,
      },
      {
        key: STEPS.P60,
        title: 'P60s',
        helperText: 'Schedule sending',
        icon: FileTextOutlined,
        content: <P60Step />,
      },
      {
        key: STEPS.SUMMARY,
        title: 'Summary',
        helperText: 'Ready for new year',
        icon: CheckCircleOutlined,
        content: <SummaryStep yearEndProcess={yearEndProcess} companyParentId={user?.companyParentId} />,
      },
    ],
    [yearEndProcess, user?.companyParentId],
  );

  const activeStepIndex = stepMenu.findIndex((s) => s.key === activeStep);
  const lastBeforeProcess = stepMenu.findIndex((s) => s.key === STEPS.P60);
  const lastStep = stepMenu.findIndex((s) => s.key === STEPS.SUMMARY);

  const handleBack = () => {
    if (activeStepIndex > 0) {
      setActiveStep(stepMenu[activeStepIndex - 1].key);
    }
  };

  const handleNext = () => {
    if (activeStepIndex < stepMenu.length - 1) {
      setActiveStep(stepMenu[activeStepIndex + 1].key);
    }
  };

  const handleConfirmProceed = async () => {
    try {
      const res = await (dispatch as any)(
        appActions.processYearEnd({ p60Date: p60issueDate }),
      ).unwrap();

      const body = res?.data;
      if (!body?.success) {
        message.error(body?.message || 'Process failed');
        setOpenConfirmModal(false);
        return;
      }

      if (body.data) {
        setYearEndProcess(body.data);
        setActiveStep(STEPS.SUMMARY);
      }
      setOpenConfirmModal(false);
      (dispatch as any)(appActions.getAppConfigs());
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          'An unknown error occurred',
      );
      setOpenConfirmModal(false);
    }
  };

  // Socket listener
  useEffect(() => {
    const handler = (res: any) => {
      const newProcess = res?.data;
      if (newProcess?.taxYear === initialTaxYear) {
        setYearEndProcess(newProcess);
        if (activeStep !== STEPS.SUMMARY) {
          setActiveStep(STEPS.SUMMARY);
        }
      }
    };

    const channel = `year-end-process-${user?.companyParentId}`;
    socketYearEndProcess.on(channel, handler);
    return () => {
      socketYearEndProcess.off(channel, handler);
    };
  }, [user?.companyParentId, initialTaxYear]);

  return (
    <KickOffContext.Provider value={contextValue}>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        centered
        width={880}
        destroyOnClose
      >
        <div
          style={{
            fontSize: '1.25rem',
            textAlign: 'center',
            marginBottom: 32,
            padding: '0 16px',
          }}
        >
          🎉Wrapping Up the {currentTaxYearStr} tax year & Kicking Off{' '}
          {nextTaxYearStr}! 🚀
        </div>
        <div style={{ display: 'flex', gap: 24 }}>
          {/* Left sidebar: step menu */}
          <div
            style={{
              width: '25%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
            >
              {stepMenu.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = activeStep === step.key;
                const isCompleted = index < activeStepIndex;

                return (
                  <div
                    key={step.key}
                    style={{ display: 'flex', gap: 10, alignItems: 'center' }}
                  >
                    <div
                      style={{
                        padding: 6,
                        borderRadius: 8,
                        backgroundColor: isActive
                          ? '#00795B'
                          : isCompleted
                            ? 'rgba(0, 121, 91, 0.16)'
                            : '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.3s ease',
                      }}
                    >
                      <StepIcon
                        style={{
                          color: isActive
                            ? 'white'
                            : isCompleted
                              ? '#00795B'
                              : '#2F2B3D',
                          fontSize: 18,
                          transition: 'color 0.3s ease',
                        }}
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: '0.9rem', color: '#2F2B3DE5' }}>
                        {step.title}
                      </div>
                      {step.helperText && (
                        <div
                          style={{
                            color: '#2F2B3DB2',
                            fontSize: '0.75rem',
                          }}
                        >
                          {step.helperText}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <img
              src={isCompleteNewYear ? KickOffCongratsImg : KickOffRocketImg}
              alt="kick-off"
              style={{
                width: 188,
                height: 180,
                objectFit: 'contain',
                marginLeft: -24,
                marginBottom: -20,
              }}
            />
          </div>

          {/* Right content: active step with fade animation */}
          <div
            key={activeStep}
            style={{
              flex: 1,
              paddingBottom: 16,
              animation: 'kickOffFadeIn 0.3s ease-in-out',
            }}
          >
            {stepMenu[activeStepIndex]?.content}
            {activeStepIndex !== lastStep && (
              <div
                style={{
                  display: 'flex',
                  justifyContent:
                    activeStepIndex === 0 ? 'flex-end' : 'space-between',
                  marginTop: 20,
                }}
              >
                {activeStepIndex !== 0 && (
                  <Button onClick={handleBack}>
                    <LeftOutlined /> Back
                  </Button>
                )}
                {activeStepIndex !== lastBeforeProcess && (
                  <Button type="primary" onClick={handleNext}>
                    Continue <RightOutlined />
                  </Button>
                )}
                {activeStepIndex === lastBeforeProcess && (
                  <Button
                    type="primary"
                    onClick={() => setOpenConfirmModal(true)}
                  >
                    Process <RightOutlined />
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>
      <ConfirmModal
        open={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        onConfirm={handleConfirmProceed}
      />
      <style>{`
        @keyframes kickOffFadeIn {
          from { opacity: 0; transform: translateX(12px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </KickOffContext.Provider>
  );
};

export default ModalKickOff;
